import type {
  App,
  SentilinkData,
} from "manager/hooks/application-summary/mappers/interfaces";

import {
  labelTemplates,
  qualifyingStatuses,
  issueDetectors,
} from "../../constants";

import { matchKeywords } from "../../queryHelpers";

const SENTILINK_RISK_TIER_PRIORITY: Record<
  NonNullable<SentilinkData["riskTier"]>,
  number
> = {
  LOW_RISK: 1,
  NEEDS_REVIEW: 2,
  HIGH_RISK: 3,
};

const FRAUD_RISK_LINE_PREFIX = "Identity Risk";

const SENTILINK_RISK_TIER_LABEL: Record<
  NonNullable<SentilinkData["riskTier"]>,
  string
> = {
  LOW_RISK: "Low",
  NEEDS_REVIEW: "Needs Review",
  HIGH_RISK: "High",
};

function isSentilinkError(sentilink: SentilinkData): boolean {
  return sentilink.outcome === "ERROR";
}

function worstRiskTier(
  tiers: Array<NonNullable<SentilinkData["riskTier"]>>
): NonNullable<SentilinkData["riskTier"]> | null {
  if (tiers.length === 0) {
    return null;
  }
  return tiers.reduce((worst, tier) =>
    SENTILINK_RISK_TIER_PRIORITY[tier] > SENTILINK_RISK_TIER_PRIORITY[worst]
      ? tier
      : worst
  );
}

/**
 * Returns the SentiLink summary line for the ID Verification card, or null when
 * SentiLink should not be shown (not triggered, old apps, all opted out, etc.).
 */
export function getSentilinkLabel(applications?: App[]): string | null {
  if (!applications?.length) {
    return null;
  }

  const sentilinkRecords = applications
    .map((app) => app?.sentilink)
    .filter((sentilink): sentilink is SentilinkData => sentilink !== undefined);

  // Pre-SentiLink deals: API omits sentilink fields on every application.
  if (sentilinkRecords.length === 0) {
    return null;
  }

  const triggered = sentilinkRecords.filter((sentilink) => sentilink.triggered);
  if (triggered.length === 0) {
    return null;
  }

  // Manually verified applicants no longer contribute their original risk tier
  // to the aggregate line (PRODUCT-2164); durable verify makes identity Pass.
  const active = triggered.filter(
    (sentilink) => !sentilink.identityManuallyVerified
  );

  if (active.length === 0) {
    return `${FRAUD_RISK_LINE_PREFIX}: Manually verified`;
  }

  if (active.some(isSentilinkError)) {
    return `${FRAUD_RISK_LINE_PREFIX}: Error`;
  }

  const tiers = active
    .map((sentilink) => sentilink.riskTier)
    .filter((tier): tier is NonNullable<SentilinkData["riskTier"]> => tier != null);

  const worst = worstRiskTier(tiers);
  if (!worst) {
    // Triggered active applicants with no tier yet (e.g. in-flight) — do not
    // fall back to "Manually verified" just because a peer was verified; that
    // would hide remaining unverified checks on multi-applicant deals.
    return null;
  }

  return `${FRAUD_RISK_LINE_PREFIX}: ${SENTILINK_RISK_TIER_LABEL[worst]}`;
}

/**
 * Appends the SentiLink line below the Lumen-driven label when applicable.
 */
export function appendSentilinkToLabel(
  baseLabel: string,
  applications?: App[]
): string {
  const sentilinkLine = getSentilinkLabel(applications);
  if (!sentilinkLine) {
    return baseLabel;
  }
  return `${baseLabel}\n${sentilinkLine}`;
}

/**
 * Gets a basic category label based on category type and status
 * Use this for categories with simple static labels
 */
export function getBaseCategoryLabel(
  categoryType: string,
  status: string
): string {
  const baseLabels = labelTemplates[categoryType] || labelTemplates.rental;
  return baseLabels[status] || "Status Unknown";
}

/**
 * Generate a detailed label based on outcome details
 * Used for categories where we want to analyze specific issues from Lumen
 */
export function getOutcomeBasedLabel(
  categoryType: string,
  status: string,
  allOutcomes: Array<{ name?: string; vero1Cleared?: boolean }>
): string {
  const outcomes = Array.isArray(allOutcomes)
    ? allOutcomes.filter((outcome) => !outcome?.vero1Cleared)
    : allOutcomes;
  // Skip if status doesn't qualify or no outcomes available
  const categoryQualifiers = qualifyingStatuses[categoryType];
  if (
    !categoryQualifiers ||
    !categoryQualifiers.includes(status) ||
    !outcomes ||
    !Array.isArray(outcomes) ||
    outcomes.length === 0
  ) {
    return null;
  }

  // Get the appropriate detectors for this category
  const detectors = issueDetectors[categoryType];
  if (!detectors || detectors.length === 0) {
    return null;
  }

  // Detect issues for this category using our generalized function
  const issues = detectors
    .filter((detector) =>
      matchKeywords(outcomes, detector.keywords, {
        property: "name",
        matchAll: true,
        matchWholeWords: false,
        caseSensitive: false,
        checkProperties: [],
      })
    )
    .map((detector) => detector.label);

  // Return comma-separated issues if any were found
  if (issues.length > 0) {
    return issues.join(", ");
  }

  return null;
}

/**
 * Determines whether to use "Total" or "Avg" prefix based on formula name
 * @param formulaName The formula name to check
 * @returns The appropriate label prefix
 */
export function getLabelPrefix(formulaName: string | null | undefined): string {
  if (!formulaName || typeof formulaName !== "string") {
    return "Avg"; // Default to "Avg" if no formula name available
  }

  const formulaLower = formulaName.toLowerCase();
  if (formulaLower.includes("total")) {
    return "Total";
  }

  if (formulaLower.includes("average") || formulaLower.includes("avg")) {
    return "Avg";
  }

  return "Avg"; // Default to "Avg" for any other cases
}

/**
 * Creates a numeric label with appropriate prefix based on formula name
 * @param type The category type (income, credit)
 * @param value The numeric value to display
 * @param formulaName The formula name to determine prefix from
 * @returns A formatted label with the appropriate prefix
 */
export function getNumericLabel(
  type: string,
  value: number,
  formulaName: string | null | undefined
): string {
  const prefix = getLabelPrefix(formulaName);

  if (type === "income-to-rent") {
    return `${prefix} Income to Rent Ratio: ${value}`;
  }

  if (type === "cash-runway") {
    return `Cash Runway Ratio: ${Number(value).toFixed(2)}`;
  }

  if (type === "credit") {
    return `${prefix} Credit Score: ${value}`;
  }

  return `${prefix} Value: ${value}`;
}
